import { useQueries } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Alert, Badge, Button, Card, EmptyState, Page, PageHeader, Table, backLinkClasses, linkTextClasses } from '@evogard/ui';
import {
  csvCell,
  csvContent,
  csvDecimal,
  format as formatMoney,
  formatInstant,
  loadFailureMessage,
  sum,
  toColumn,
  ZERO,
  type Money,
} from '@evogard/domain';
import type { BillingAwardPeriodView } from '@evogard/contract';
import { api } from '../../../lib/api.js';
import { saveCsv } from '../../../lib/csv.js';
import { AWARDS_KEY, mergeAwards, periodLabel, useAwardTenants, type AwardTenant, type AwardTenantState } from './useAwards.js';

/** Uma competência no relatório: o que ela pagou, ou por que não dá para dizer. */
interface HistoryRow {
  period: string;
  situation: 'legado' | 'fechada' | 'aberta';
  byTenant: Record<string, Money>;
  recovered: Money;
  paid: number;
  commission: Money;
  leadership: Money;
  /** Por que o valor não sai — a régua das bases diverge, ou uma base não respondeu. */
  problem: string | null;
}

/**
 * O relatório por competência — uma linha por mês registrado (pedido do dono,
 * 23/10/2026): o recuperado, a comissão do time e a da liderança.
 *
 * O mês trazido do sistema antigo mostra o valor congelado de lá; o fechado
 * aqui, o cálculo sobre o que ficou congelado; o aberto, a prévia. É a mesma
 * conta do painel (`mergeAwards`), mês a mês — duas contas fariam o relatório
 * discordar do painel do mês.
 */
export function AwardsHistoryScreen() {
  const tenants = useAwardTenants();
  const lists = useQueries({
    queries: tenants.map((t) => ({
      queryKey: [...AWARDS_KEY, 'competencias', t.slug],
      queryFn: async () => {
        const r = await api.billingAwards.periods({ params: { tenant: t.slug } });
        if (r.status === 403) return null;
        if (r.status !== 200) throw new Error(loadFailureMessage(r.status, `as competências da ${t.name}`));
        return r.body.items;
      },
    })),
  });
  const periods = [...new Set(lists.flatMap((l) => (l.data ?? []).map((i) => i.period)))].sort().reverse();
  const pairs = periods.flatMap((period) => tenants.map((tenant) => ({ period, tenant })));
  const views = useQueries({
    queries: pairs.map(({ period, tenant }) => ({
      queryKey: [...AWARDS_KEY, 'competencia', tenant.slug, period],
      queryFn: async (): Promise<BillingAwardPeriodView | null> => {
        const r = await api.billingAwards.period({ params: { tenant: tenant.slug }, query: { period } });
        if (r.status === 403) return null;
        if (r.status !== 200) throw new Error(loadFailureMessage(r.status, `a premiação da ${tenant.name}`));
        return r.body;
      },
    })),
  });

  const loading = lists.some((l) => l.isLoading) || views.some((v) => v.isLoading);
  const failed = [...lists, ...views].find((q) => q.error)?.error?.message ?? null;
  const rows = periods.map((period) =>
    rowOf(
      period,
      tenants,
      tenants.map((tenant): AwardTenantState => {
        const q = views[pairs.findIndex((p) => p.period === period && p.tenant.slug === tenant.slug)]!;
        if (q.error) return { tenant, state: 'erro', message: q.error.message };
        if (q.data === null) return { tenant, state: 'sem_permissao' };
        if (q.data) return { tenant, state: 'pronta', view: q.data };
        return { tenant, state: 'carregando' };
      }),
    ),
  );

  return (
    <Page measure="cheia">
      <PageHeader
        title="Relatório por competência"
        back={
          <Link to="/billing/awards" className={backLinkClasses()}>
            Premiação da cobrança
          </Link>
        }
      />
      <p className="text-sm text-texto-suave">
        Uma linha por mês com registro, das bases {tenants.map((t) => t.name).join(' e ')} somadas. O mês trazido do
        sistema antigo mostra o valor que ele mostrava, congelado; o fechado aqui, o que ficou congelado no fechamento; o
        aberto, a prévia.
      </p>
      {failed && <Alert tone="erro">{failed}</Alert>}

      {loading && rows.length === 0 ? (
        <p className="text-sm text-texto-suave">Carregando as competências…</p>
      ) : rows.length === 0 ? (
        <EmptyState icon="grafico" title="Nenhuma competência registrada">
          Ainda não há mês fechado nem vínculo calculado nas suas bases.
        </EmptyState>
      ) : (
        <Card
          title="Competências"
          descricao="Comissão do time é a soma das bonificações diária, semanal e mensal; a da liderança vem à parte."
          bleed
        >
          <Table<HistoryRow>
            label="Premiação por competência"
            empty="Nenhuma competência registrada."
            items={rows}
            rowKey={(r) => r.period}
            paginate={false}
            columns={[
              {
                title: 'Competência',
                alignment: 'inicio',
                cell: (r) => (
                  <Link to="/billing/awards" search={{ period: r.period }} className={linkTextClasses('font-medium')}>
                    {periodLabel(r.period)}
                  </Link>
                ),
              },
              { title: 'Situação', alignment: 'inicio', cell: (r) => <SituationBadge situation={r.situation} /> },
              ...tenants.map((t) => ({
                title: t.slug.toUpperCase(),
                alignment: 'fim' as const,
                cell: (r: HistoryRow) => (r.problem ? '—' : formatMoney(r.byTenant[t.slug] ?? ZERO)),
              })),
              { title: 'Recuperado', alignment: 'fim', cell: (r) => (r.problem ? '—' : formatMoney(r.recovered)) },
              { title: 'Pagos', alignment: 'fim', cell: (r) => (r.problem ? '—' : r.paid) },
              {
                title: 'Comissão do time',
                alignment: 'fim',
                cell: (r) => (r.problem ? <span className="text-texto-suave">{r.problem}</span> : formatMoney(r.commission)),
              },
              { title: 'Liderança', alignment: 'fim', cell: (r) => (r.problem ? '—' : formatMoney(r.leadership)) },
              {
                title: 'Total',
                alignment: 'fim',
                cell: (r) => (r.problem ? '—' : <strong>{formatMoney(sum(r.commission, r.leadership))}</strong>),
              },
            ]}
          />
          <div className="px-4 py-3">
            <Button variant="contorno" disabled={loading || rows.length === 0} onClick={() => download(rows, tenants)}>
              Exportar CSV
            </Button>
          </div>
        </Card>
      )}
    </Page>
  );
}

function SituationBadge({ situation }: { situation: HistoryRow['situation'] }) {
  if (situation === 'legado') return <Badge tone="neutro">Sistema antigo</Badge>;
  if (situation === 'fechada') return <Badge tone="sucesso">Fechada</Badge>;
  return <Badge tone="informacao">Aberta — prévia</Badge>;
}

/** Uma competência, pela mesma conta do painel. */
function rowOf(period: string, tenants: readonly AwardTenant[], states: readonly AwardTenantState[]): HistoryRow {
  const ready = states.flatMap((s) => (s.state === 'pronta' ? [s] : []));
  const situation: HistoryRow['situation'] = ready.some((s) => s.view.closing?.source === 'premiacao_legado')
    ? 'legado'
    : ready.length > 0 && ready.every((s) => s.view.closing !== null)
      ? 'fechada'
      : 'aberta';
  const empty = { period, situation, byTenant: {}, recovered: ZERO, paid: 0, commission: ZERO, leadership: ZERO };
  if (states.some((s) => s.state === 'carregando')) return { ...empty, problem: 'carregando…' };
  const merged = mergeAwards(states);
  if (merged.state === 'recusado') return { ...empty, problem: 'a régua das bases não é a mesma' };
  if (merged.state === 'vazio') return { ...empty, problem: 'nenhuma base respondeu' };
  /* o mês do sistema antigo já traz as duas bases no valor congelado; o daqui precisa das duas */
  if (!merged.frozen && merged.missing.length > 0) {
    return { ...empty, problem: `falta ${merged.missing.map((t) => t.name).join(', ')}` };
  }
  const operators = merged.result.operators;
  const byTenant: Record<string, Money> = {};
  for (const t of tenants) {
    byTenant[t.slug] = sum(...operators.map((o) => o.byTenant.find((b) => b.tenantId === t.id)?.recovered ?? ZERO));
  }
  return {
    period,
    situation,
    byTenant,
    recovered: sum(...operators.map((o) => o.recovered)),
    paid: operators.reduce((n, o) => n + o.paidCount, 0),
    commission: sum(...operators.map((o) => o.total)),
    leadership: merged.result.leadership?.bonus ?? ZERO,
    problem: null,
  };
}

// @valor
const SITUATION_LABELS: Record<HistoryRow['situation'], string> = {
  legado: 'Sistema antigo',
  fechada: 'Fechada',
  aberta: 'Aberta — prévia',
};

function download(rows: readonly HistoryRow[], tenants: readonly AwardTenant[]): void {
  const money = (m: Money) => csvDecimal(toColumn(m));
  const content = csvContent([
    [
      'Competência',
      'Situação',
      ...tenants.map((t) => `Recuperado ${t.slug.toUpperCase()}`),
      'Recuperado',
      'Pagos',
      'Comissão do time',
      'Liderança',
      'Total',
    ].map(csvCell),
    ...rows.map((r) =>
      r.problem
        ? [csvCell(periodLabel(r.period)), csvCell(SITUATION_LABELS[r.situation]), csvCell(r.problem)]
        : [
            csvCell(periodLabel(r.period)),
            csvCell(SITUATION_LABELS[r.situation]),
            ...tenants.map((t) => money(r.byTenant[t.slug] ?? ZERO)),
            money(r.recovered),
            csvCell(String(r.paid)),
            money(r.commission),
            money(r.leadership),
            money(sum(r.commission, r.leadership)),
          ],
    ),
    [],
    [csvCell('Bases'), csvCell(tenants.map((t) => t.name).join(' e '))],
    [csvCell('Gerado em'), csvCell(formatInstant(new Date().toISOString()))],
  ]);
  saveCsv(content, 'premiacao-cobranca-por-competencia', tenants.map((t) => t.slug).join('-'));
}
